import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';
import {
  ARRANGED_CALLBACKS_LOCALSTORAGE_TOKEN,
  AuthStore,
  Helpers,
  InstallationsStore,
  LanguageStore,
  ServiceTicketArrangedCallback,
  ServiceTicketBoilerSerialErrors,
  ServiceTicketCallType,
  ServiceTicketError,
  ServiceTicketStore,
} from '@store';
import { ActiveErrorsService } from '../../+installations/services/active-errors.service';
import {
  BffInstallationsApi,
  EquipmentApiPicker,
  InstallationItem,
  ServiceDocumentsPicker,
  ViError,
  forbiddenEquipmentMaterialGroups,
} from '../../../projects/client';
import { AssistantFeedbackParams } from '../../../projects/client/apis/assistant/assistant.api-model';
import { AssistantApiPicker } from '../../../projects/client/apis/assistant/assistant.api-picker';
import { ViToastService } from '../../../projects/ui';
import {
  AppServiceType,
  AssistantAction,
  AssistantStore,
  AssistantSupportedLanguages,
} from '../assistant';
import { AssistantHelperService } from '../assistant/assistant.helper.service';
import { ServiceTicketActions } from './service-ticket.actions';
import { ServiceTicketHelper } from './service-ticket.helper';

@Injectable()
export class ServiceTicketEffects {
  updateMarketingName$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.updateSearchedBoilerSerial.attempt),
      concatLatestFrom(() => [
        this.serviceTicketStore
          .boilerSerialAutocompleteDevices()
          .pipe(filter((data) => data.length > 0)),
      ]),
      switchMap(([action, devices]) => {
        return of(action.boilerSerial).pipe(
          switchMap((serial) => {
            const matchedDevice = devices.find((entry) => entry.serial === serial);
            const marketingName = !!matchedDevice ? matchedDevice.marketingName : serial;

            return of(ServiceTicketActions.updateMarketingName.success({ marketingName }));
          }),
          catchError((error) => of(ServiceTicketActions.updateMarketingName.error({ error })))
        );
      })
    );
  });

  validateBoilerSerial$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.updateSearchedBoilerSerial.attempt),
      switchMap((action) => {
        // 1. validate boiler serial
        if (isNaN(+action.boilerSerial)) {
          return of(
            ServiceTicketActions.validateBoilerSerial.error({
              error: ServiceTicketBoilerSerialErrors.NonNumericValue,
            })
          );
        }

        const liteDetailRequest$ = this.equipmentApi.instance().liteDetail(action.boilerSerial);

        const validateBoilerSerialRequest$ = this.equipmentApi
          .instance()
          .validateBoilerSerial(action.boilerSerial);

        return forkJoin([liteDetailRequest$, validateBoilerSerialRequest$]).pipe(
          map(([liteDetail]) => {
            const materialGroup = liteDetail?.materialGroup;

            const isBoilerSerialEntered = forbiddenEquipmentMaterialGroups.every(
              (forbiddenMaterialGroup) => forbiddenMaterialGroup !== materialGroup
            );

            if (isBoilerSerialEntered) {
              // 1.1 in case of success OK 200 return success
              return ServiceTicketActions.validateBoilerSerial.success({
                boilerSerial: action.boilerSerial,
              });
            } else {
              // 1.2 user entered a wrong type of serial
              return ServiceTicketActions.validateBoilerSerial.gatewaySerialInsteadOfBoilerError();
            }
          }),
          catchError((error) => {
            return of(ServiceTicketActions.validateBoilerSerial.error({ error }));
          })
        );
      })
    );
  });

  onValidateBoilerSerialSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.validateBoilerSerial.success),
      switchMap((action) => {
        return of(
          ServiceTicketActions.loadBoilerDeviceDetails.attempt({
            boilerSerial: action.boilerSerial,
          })
        );
      })
    );
  });

  onBypassBoilerSerialValidation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.bypassBoilerSerialValidation.attempt),
      concatLatestFrom(() => [this.serviceTicketStore.searchedBoilerSerial$]),
      switchMap(([_action, boilerSerial]) => {
        return of(
          ServiceTicketActions.loadBoilerDeviceDetails.attempt({
            boilerSerial,
          })
        );
      })
    );
  });

  loadBoilerDeviceDetailsAttempt$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.loadBoilerDeviceDetails.attempt),
      concatLatestFrom(() => [this.installationsStore.installations$]),
      // 2. Load all errors details based on boiler serial
      // these errors are needed to display detailed information about an error
      switchMap(([action, installations]) => {
        const errorParams = this.activeErrorsService.getSearchActiveErrorParameters(
          action.boilerSerial
        );

        return this.serviceDocumentsPicker
          .instance()
          .listErrorsDatabase({
            materialNumber: errorParams?.serial,
            countryCode: errorParams?.countryCode,
            languageCode: errorParams?.langCode,
          })
          .pipe(
            switchMap((allErrors) => {
              return of(
                ServiceTicketActions.loadBoilerDeviceDetails.loadErrors({
                  boilerSerial: action.boilerSerial,
                  installations,
                  allErrors,
                  errorParams,
                })
              );
            }),
            catchError((error) => {
              return of(ServiceTicketActions.loadBoilerDeviceDetails.error({ error }));
            })
          );
      })
    );
  });

  loadBoilerDeviceDetailsLoadErrors$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.loadBoilerDeviceDetails.loadErrors),
      switchMap((action) => {
        // 3. find gateway of boiler serial to access details such as installationId
        const installation: InstallationItem = action.installations?.find((installationEntry) =>
          installationEntry?.gateways.some((gatewayEntry) =>
            gatewayEntry?.devices.find(
              (deviceEntry) => deviceEntry.serial && deviceEntry.serial === action.boilerSerial
            )
          )
        );

        if (!installation) {
          return of(
            ServiceTicketActions.loadBoilerDeviceDetails.error({
              error: 'No installation matched by boiler serial',
            })
          );
        }

        // 4. list all possible errors on given boiler serial device
        return this.equipmentApi
          .instance()
          .listActiveError(installation?.id)
          .pipe(
            map((activeErrorsResponse) => {
              const activeErrorsCodes = activeErrorsResponse.data.map((error) => {
                return error.content.errorCode;
              });
              const isResponseLangDifferentThanUserLanguage =
                action.allErrors.countryCode !== action.errorParams.countryCode ||
                action.allErrors.languageCode !== action.errorParams.langCode;

              // 5. Extend error details with additional description
              // const richErrorsDetails = []
              const richErrorsDetails: ServiceTicketError[] = action.allErrors.faultCodes
                .filter((entry) => {
                  return activeErrorsCodes.includes(entry.faultCode);
                })
                .map((entry) => {
                  return {
                    faultCode: entry.faultCode,
                    systemCharacteristics: entry.systemCharacteristics,
                    causes: entry?.causes,
                    text: ServiceTicketHelper.faultCodeTextMapper(entry),
                  };
                });

              return ServiceTicketActions.loadBoilerDeviceDetails.success({
                errorsDetails: {
                  detailsInDifferentLanguage: isResponseLangDifferentThanUserLanguage,
                  details: richErrorsDetails,
                },
              });
            }),
            catchError((error) => {
              return of(ServiceTicketActions.loadBoilerDeviceDetails.error({ error }));
            })
          );
      })
    );
  });

  onFeedbackUpdated$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.updateFeedback.attempt),
      debounceTime(1000),
      switchMap(() => {
        return of(ServiceTicketActions.updateFeedback.success());
      })
    );
  });

  routingProvider$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.routingProviderRequest.attempt),
      switchMap((action) => {
        return this.bffInstallationsApi.getRoutingProvider(action.boilerSerial).pipe(
          map((response) => {
            return ServiceTicketActions.routingProviderRequest.success({
              routingProvider: response.routingProvider,
            });
          }),
          catchError((error: ViError) => {
            const message = this.translateService.instant(
              '+SERVICE_TICKET.MESSAGE_REQUEST_ROUTING_PROVIDER_ERROR'
            );
            this.toastService.error({
              message,
            });

            return of(ServiceTicketActions.routingProviderRequest.error({ error }));
          })
        );
      })
    );
  });

  requestWaitingTime$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.requestWaitingTime.attempt),
      concatLatestFrom(() => this.serviceTicketStore.errorTicketDetails$),
      switchMap(([action, ticketDetails]) => {
        return this.bffInstallationsApi.postWaitingTime(ticketDetails.boilerSerial).pipe(
          map((response) => {
            return ServiceTicketActions.requestWaitingTime.success({
              waitTime: response.waitTime,
              callType: action.callType,
            });
          }),
          catchError((error) => {
            const message = this.translateService.instant(
              '+SERVICE_TICKET.MESSAGE_REQUEST_WAITING_TIME_ERROR'
            );
            this.toastService.error({
              message,
            });

            return of(ServiceTicketActions.requestWaitingTime.error(error));
          })
        );
      })
    );
  });

  onWaitingTimeSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.requestWaitingTime.success),
      switchMap((action) => {
        if (action.callType === ServiceTicketCallType.ArrangedCallback) {
          return of(ServiceTicketActions.arrangedCallbackRequest.attempt());
        } else if (action.callType === ServiceTicketCallType.CallNow) {
          return of(ServiceTicketActions.callNowRequest.attempt());
        }
      })
    );
  });

  callNowRequestAttempt$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.callNowRequest.attempt),
      concatLatestFrom(() => [
        this.serviceTicketStore.errorTicketDetails$,
        this.authStore.getPhone(),
      ]),
      switchMap(([_action, errorTicketDetails, userPhoneNumber]) => {
        const errorCodeArray = [
          ...(errorTicketDetails?.errorCode ?? []),
          ...(errorTicketDetails?.otherErrorCode ? [errorTicketDetails?.otherErrorCode] : []),
        ];

        return this.bffInstallationsApi
          .postPhone(
            errorTicketDetails.boilerSerial,
            userPhoneNumber,
            errorCodeArray,
            errorTicketDetails.additionalFeedback
          )
          .pipe(
            map((response) =>
              ServiceTicketActions.callNowRequest.success({ acdNumber: response.acdNumber })
            ),
            catchError((error) => {
              const message = this.translateService.instant(
                '+SERVICE_TICKET.MESSAGE_CALL_NOW_ERROR'
              );
              this.toastService.error({
                message,
              });

              return of(ServiceTicketActions.callNowRequest.error({ error }));
            })
          );
      })
    );
  });

  arrangedCallbackRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.arrangedCallbackRequest.attempt),
      concatLatestFrom(() => [
        this.serviceTicketStore.errorTicketDetails$,
        this.serviceTicketStore.errorCodes$,
      ]),
      switchMap(([_action, errorTicketDetails, storeErrorCodes]) => {
        const errorCodes: string[] = storeErrorCodes.errorCode ?? [];
        if (storeErrorCodes.otherErrorCode) {
          errorCodes.push(storeErrorCodes.otherErrorCode);
        }

        return this.bffInstallationsApi
          .postCallback(
            errorTicketDetails.boilerSerial,
            errorTicketDetails.phoneNumber,
            !!errorCodes.length ? errorCodes : null,
            errorTicketDetails.additionalFeedback
          )
          .pipe(
            map((response) => {
              const postCallbackTime = Helpers.getProperCallbackTimeFormat(
                errorTicketDetails.waitTime
              );
              const dismissTime = Helpers.getProperCallbackTimeFormat(
                errorTicketDetails.waitTime + 3600
              );

              const arrangedCallback: ServiceTicketArrangedCallback = {
                id: response.id,
                device: errorTicketDetails.boilerSerial,
                time: postCallbackTime,
                dismissTime,
                timestamp: +new Date(),
              };

              return ServiceTicketActions.arrangedCallbackRequest.success({ arrangedCallback });
            }),
            catchError((error) => {
              const message = this.translateService.instant(
                '+SERVICE_TICKET.MESSAGE_ARRANGED_CALLBACK_ERROR'
              );
              this.toastService.error({
                message,
              });

              return of(ServiceTicketActions.arrangedCallbackRequest.error(error));
            })
          );
      })
    );
  });

  loadCachedCallbacks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.loadCachedCallbacks.attempt),
      map(() => {
        let cachedCallbacks: ServiceTicketArrangedCallback[] = JSON.parse(
          localStorage.getItem(ARRANGED_CALLBACKS_LOCALSTORAGE_TOKEN)
        );

        if (cachedCallbacks && cachedCallbacks.length) {
          const nextCallbacks = [];
          cachedCallbacks.forEach((callback) => {
            if (this.isRequestValid(callback)) {
              nextCallbacks.push(callback);
            }
          });
          cachedCallbacks = nextCallbacks;

          return ServiceTicketActions.loadCachedCallbacks.success({ cachedCallbacks });
        } else {
          return ServiceTicketActions.loadCachedCallbacks.error();
        }
      }),
      catchError(() => of(ServiceTicketActions.loadCachedCallbacks.error()))
    );
  });

  // Communication with Assistant Store
  onAttemptSearchDocumentsByErrorCode$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.updateSelectedErrorCodes.attempt),
      filter((action) => !!action.errorCodes),
      concatLatestFrom(() => [
        this.serviceTicketStore.errorCodes$,
        this.authStore.isCompanyFromGermanyOrAustria$,
      ]),
      switchMap(([action, errorCodes, isCompanyFromGermanyOrAustria]) => {
        let query = '';

        if (!!action.errorCodes) {
          query += action.errorCodes.join(', ');
        }

        if (errorCodes.otherErrorCode) {
          if (query !== '') {
            query += `, ${errorCodes.otherErrorCode}`;
          } else {
            query += `${errorCodes.otherErrorCode}`;
          }
        }

        return of(
          AssistantAction.searchForDocuments.attempt({
            text: query,
            // Hardcoded since Service Ticket is available only for DE users
            filtersPayload: {
              ...(isCompanyFromGermanyOrAustria && { esc_Languages: ['de'] }),
              InformationType: ['Solution database\u001fError description'],
            },
            limit: 5,
            app: AppServiceType.ViGuideServiceticket,
          })
        );
      })
    );
  });

  onAttemptSearchDocumentsByOtherCode$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.updateOtherErrorCode.loadDocuments),
      concatLatestFrom(() => [
        this.serviceTicketStore.errorCodes$,
        this.languageStore.languageCode$,
        this.serviceTicketStore.searchedBoilerSerial$,
        this.authStore.isCompanyFromGermanyOrAustria$,
      ]),
      switchMap(
        ([action, errorCodes, langCode, searchedBoilerSerial, isCompanyFromGermanyOrAustria]) => {
          let query = '';

          if (!!errorCodes.errorCode) {
            query += errorCodes.errorCode.join(', ');
          }

          if (action.otherErrorCode) {
            if (query !== '') {
              query += `, ${action.otherErrorCode}`;
            } else {
              query += `${action.otherErrorCode}`;
            }
          }

          const supportedLangCode =
            this.assistantHelperService.getAssistantPayloadLanguage(langCode) ??
            AssistantSupportedLanguages.en;

          return of(
            AssistantAction.searchForDocuments.attempt({
              text: query,
              filtersPayload: {
                Materialno: [searchedBoilerSerial.slice(0, 7)],
                esc_Languages: [supportedLangCode],
                ...(isCompanyFromGermanyOrAustria && { esc_Languages: ['de'] }),
                InformationType: ['Solution database\u001fError description'],
              },
              limit: 5,
              app: AppServiceType.ViGuideServiceticket,
            })
          );
        }
      )
    );
  });

  onDocumentsLoaded$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AssistantAction.searchForDocuments.success, AssistantAction.searchForDocuments.error),
      switchMap(() => {
        return of(ServiceTicketActions.updateOtherErrorCode.onDocumentsLoaded());
      })
    );
  });

  // end

  rateSelectedArticle$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ServiceTicketActions.rateSelectedArticle.attempt),
      concatLatestFrom(() => [
        this.assistantStore.selectedDocumentState$,
        this.authStore.userId$,
        this.serviceTicketStore.articleFeedbackDetails$,
      ]),
      switchMap(([action, selectedDocument, userId, feedbackDetails]) => {
        const documentId = selectedDocument.searchDocument.id;

        const params: AssistantFeedbackParams = {
          documentTitle: selectedDocument.searchDocument.title,
          documentTypes: selectedDocument.searchDocument.documentType.label,
          feedbackInput: action.isPositive ? 'Yes' : 'No',
          serialNumber: feedbackDetails.serial,
          productName: feedbackDetails.productName,
          errorCode: feedbackDetails.errorCode.join(',') || feedbackDetails.otherErrorCode,
          rating: action.isPositive ? 5 : 1,
          timestamp: new Date().toISOString(),
          userId,
        };

        return this.assistantApiPicker
          .instance()
          .rateDocument(documentId, params)
          .pipe(
            map(() => ServiceTicketActions.rateSelectedArticle.success()),
            catchError(() => of(ServiceTicketActions.rateSelectedArticle.error()))
          );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private equipmentApi: EquipmentApiPicker,
    private installationsStore: InstallationsStore,
    private serviceDocumentsPicker: ServiceDocumentsPicker,
    private activeErrorsService: ActiveErrorsService,
    private serviceTicketStore: ServiceTicketStore,
    private bffInstallationsApi: BffInstallationsApi,
    private authStore: AuthStore,
    private translateService: TranslateService,
    private toastService: ViToastService,
    private assistantStore: AssistantStore,
    private assistantApiPicker: AssistantApiPicker,
    private languageStore: LanguageStore,
    private assistantHelperService: AssistantHelperService
  ) {}

  private isRequestValid(callback: ServiceTicketArrangedCallback): boolean {
    const currentTime = new Date().getTime();
    const callbackNotificationLifetime = 1000 * 60 * 10; // ms * s * min

    return currentTime - callback.timestamp < callbackNotificationLifetime;
  }
}
